const React = require('react');
const ReactDOM = require('react-dom');

import { ConnectionStatusView } from '../clients/app/shared/connection-status/ConnectionStatusView';
/**
 * Socrative Messaging
 *
 * A pubnub wrapper that executes listener callbacks when messages are received or presence changes.
 *
 * The following object is an example of how the listeners are represented:
 *
 * {
 *     studentResponse: [ // message type
 *         callback1,
 *         callback2,
 *         ...
 *     ],
 *     presence: [ // message type
 *         callback3,
 *         callback4,
 *         ...
 *     ],
 *     ...
 * }
 *
 */

const Constants = require('Constants');
const room = require('room');

const PubNubDown = 'PNNetworkDownCategory';
const PubNubUp = 'PNNetworkUpCategory';
const PubNubConnected = 'PNConnectedCategory';
const PubNubReconnected = 'PNReconnectedCategory';
// The SDK's subscribe retries gave up (handshake or receive loop). It looks
// the same as a network drop to the teacher, so it gets the same banner.
const PubNubConnectionError = 'PNConnectionErrorCategory';
const PubNubDisconnectedUnexpectedly = 'PNDisconnectedUnexpectedlyCategory';

const listeners = {};
/**
 * Listeners in this file and pubnub's listeners are different sets of listeners. The listeners
 * that pubnub track are presence and messages. We need an exact reference to each of the listeners
 * when we call window.pubnub.addListener so we can remove them properly using window.pubnub.removeListener
 * since pubnub's native removeListener does not do a deep comparison of objects
 */
let localCopyOfPubNubListeners = {};

// The last presence update that arrived with no PRESENCE listener, replayed
// to the first one registered (see presenceChanged).
let undeliveredPresence = null;

// A rostered student gets a new user_uuid when they log in to a fresh attempt,
// so they come online under a uuid the roster fetched at room join doesn't
// have. An unknown occupant refetches the roster; logins arriving together
// share one fetch.
const ROSTER_REFETCH_DELAY_MS = 1000;
// A uuid still unknown after this many fetches isn't a roster student (a
// stale uuid waiting out its presence timeout): stop fetching for it.
const MAX_ROSTER_FETCHES_PER_UUID = 2;
let rosterRefetch = null;
const rosterFetchesByUuid = new Map();

const cancelRosterRefetch = () => {
  if (rosterRefetch) {
    clearTimeout(rosterRefetch.timer);
    rosterRefetch = null;
  }
  rosterFetchesByUuid.clear();
};

/**
 * Private function called every time a message is received from pubnub. Executes all callbacks listening for the message.
 * @param {object} message The message received from pubnub
 */
const messageReceived = (message) => {
  const callbacks = listeners[message.key];

  if (callbacks) {
    for (const callback of callbacks) {
      callback(message.data);
    }
  }
};

/**
 * Reconcile each rostered student's `logged_in` flag against the authoritative
 * PubNub channel occupancy (`occupants` from hereNow — the same live source the
 * results grid uses via useActivity.checkStudentPresence). Bidirectional: a uuid
 * present in `occupants` is marked logged_in, everyone else logged_out.
 *
 * Why: the backend roster's `logged_in` is derived from a persistent DB status
 * bit (Python getRosteredStudents), which only flips on explicit login/logout
 * endpoint calls. A PubNub join/leave (e.g. a student closing their tab) never
 * hits those endpoints, so getStudentList's `logged_in` is stale — it keeps
 * counting students who have already left and misses students who just joined.
 * hereNow is the source of truth for who is on the channel right now.
 * @param {Array<{uuid: string}>} occupants hereNow occupants for the channel
 */
const reconcilePresence = (occupants) => {
  const presentUuids = new Set(
    (occupants || []).map((occupant) => occupant.uuid)
  );
  const students = room.getStudents() || [];
  students.forEach((student) => {
    if (student.user_uuid) {
      student.logged_in = presentUuids.has(student.user_uuid);
    }
  });
  // Shallow-clone so Backbone reliably fires change:students for the
  // StudentMenu listener.
  room.set('students', students.slice());
};

const unknownOccupants = (occupants) => {
  const rosterUuids = new Set(
    (room.getStudents() || []).map((student) => student.user_uuid)
  );
  return (occupants || [])
    .map((occupant) => occupant.uuid)
    .filter(
      (uuid) =>
        !rosterUuids.has(uuid) &&
        (rosterFetchesByUuid.get(uuid) || 0) < MAX_ROSTER_FETCHES_PER_UUID
    );
};

/**
 * Refetch the roster when someone present isn't on it, then reconcile again
 * against the latest occupants.
 * @param {Array<{uuid: string}>} occupants hereNow occupants for the channel
 * @param {string} channelName the student channel
 * @param {Function} onRefetched called with (data, channelName) to reconcile
 */
const refetchRosterForUnknownOccupants = (
  occupants,
  channelName,
  onRefetched
) => {
  if (rosterRefetch) {
    // One is already due; it reconciles against whoever is present by then.
    Object.assign(rosterRefetch, { occupants, channelName });
    return;
  }
  if (unknownOccupants(occupants).length === 0) {
    return;
  }

  const pending = { occupants, channelName };
  pending.timer = setTimeout(() => {
    const unknown = unknownOccupants(pending.occupants);
    if (unknown.length === 0) {
      rosterRefetch = null;
      return;
    }
    unknown.forEach((uuid) =>
      rosterFetchesByUuid.set(uuid, (rosterFetchesByUuid.get(uuid) || 0) + 1)
    );
    room.getStudentList({
      success: () => {
        if (rosterRefetch !== pending) {
          return; // The room changed while it was in flight.
        }
        rosterRefetch = null;
        // getStudentList reset logged_in to the DB value: reconcile again.
        onRefetched(
          {
            occupancy: pending.occupants.length,
            occupants: pending.occupants,
          },
          pending.channelName
        );
      },
      error: () => {
        if (rosterRefetch === pending) {
          rosterRefetch = null;
        }
      },
    });
  }, ROSTER_REFETCH_DELAY_MS);
  rosterRefetch = pending;
};

/**
 * Private function called every time the presence changes. Executes all callbacks listening for presence.
 * @param {object} data The presence data received from pubnub
 */
const presenceChanged = (data, channelName) => {
  const callbacks = listeners[Constants.PRESENCE];
  let channelParts = channelName ? channelName.split('-') : [];
  let roomName = channelParts.length >= 2 ? channelParts[0] : null;

  if (!callbacks || callbacks.length === 0) {
    // Nobody listening yet (the header registers after the boot-time room
    // join, and the first presence seed can beat it). Hold the latest update
    // for the first listener: the teacher isn't an occupant of the student
    // channel, so no self-join event follows to re-deliver it, and the
    // presence store only re-emits when who is present changes.
    undeliveredPresence = { data, channelName };
    return;
  }
  undeliveredPresence = null;

  // Non-rostered rooms have no per-student list to reconcile; let the header's
  // occupancy-only branch (HeaderController.presenceChanged) handle the count.
  if (!room.hasRoster()) {
    for (const callback of callbacks) {
      callback(data, roomName);
    }
    return;
  }

  const runCallbacks = () => {
    for (const callback of callbacks) {
      callback(data, roomName);
    }
  };

  // Reconcile logged_in against live occupancy, then notify. The roster is
  // fetched on room join and roster edits, and again only when someone
  // present isn't on it (a student who logged in under a new uuid), not on
  // every presence event. Per-student presence dots read the shared
  // PresenceStore (~shared/realtime); this Backbone path remains for the
  // header count and as the store's fallback value.
  if (data && Array.isArray(data.occupants)) {
    // Connect/reconnect path: hereNow already provided the occupant list.
    reconcilePresence(data.occupants);
    runCallbacks();
    refetchRosterForUnknownOccupants(
      data.occupants,
      channelName,
      presenceChanged
    );
  } else if (channelName && window.pubnub) {
    // Native join/leave/timeout events carry only the single changed uuid
    // plus a headcount — never the full occupant list — so query it.
    window.pubnub.hereNow(
      { channels: [channelName], includeUUIDs: true },
      (status, resp) => {
        if (
          !status.error &&
          resp &&
          resp.channels &&
          resp.channels[channelName]
        ) {
          reconcilePresence(resp.channels[channelName].occupants);
          refetchRosterForUnknownOccupants(
            resp.channels[channelName].occupants,
            channelName,
            presenceChanged
          );
        }
        runCallbacks();
      }
    );
  } else {
    runCallbacks();
  }
};

class Messaging {
  constructor() {
    this.showStatus = false;
    this.onDismissStatus = this.onDismissStatus.bind(this);
  }

  onDismissStatus() {
    this.showStatus = false;
    ReactDOM.render(
      <ConnectionStatusView showStatus={this.showStatus} />,
      document.getElementById('status-container')
    );
  }

  pubnubMessageListener(data) {
    let message = data.message;
    try {
      message = JSON.parse(data.message);
    } catch (error) {
      console.log('Failed to parse JSON message, defaulting to string.');
    }

    messageReceived(message);
  }
  /**
   * Build reconnection logic function to pass into listener options when we subscribe.
   * Some events of the status differ based on whether we are tracking presence or messages.
   * @param {object} props object with properties needed for the reconnection logic
   * @param {object} subscribeOptions object with channels and other attributes used for resubscribing
   * @param {boolean} isSubscribeToPresence boolean based on which subscribe method was called
   */
  createPubNubStatusListener(props, subscribeOptions, isSubscribeToPresence) {
    return (statusMessage) => {
      let category = statusMessage.category;

      if (category === PubNubConnected || category === PubNubReconnected) {
        console.log('PubNub connection established.');
        ReactDOM.render(
          <ConnectionStatusView
            showStatus={this.showStatus}
            user={props.user}
            onDismiss={this.onDismissStatus}
            connectionStatus="connected"
          />,
          document.getElementById('status-container')
        );

        if (isSubscribeToPresence) {
          // On reconnect, get the current student presence
          window.pubnub.hereNow(
            {
              channels: [props.channel],
              includeUUIDs: true,
            },
            (status, resp) => {
              if (!status.error && resp && resp.channels) {
                presenceChanged(resp.channels[props.channel], props.channel);
              }
            }
          );
        }
      }

      if (
        category === PubNubDown ||
        category === PubNubConnectionError ||
        category === PubNubDisconnectedUnexpectedly
      ) {
        console.log('PubNub connection lost');
        this.showStatus = true;
        ReactDOM.render(
          <ConnectionStatusView
            showStatus={this.showStatus}
            user={props.user}
            connectionStatus="disconnected"
          />,
          document.getElementById('status-container')
        );
      }

      if (category === PubNubUp) {
        console.log('PubNub reconnecting...');
        ReactDOM.render(
          <ConnectionStatusView
            showStatus={this.showStatus}
            user={props.user}
            connectionStatus="reconnecting"
          />,
          document.getElementById('status-container')
        );
        // Teacher subscriptions are owned by the realtime channel manager
        // (subscribeOptions is null on that path) — it handles reconnects.
        if (subscribeOptions) {
          window.pubnub.subscribe(subscribeOptions);
        }
      }
    };
  }

  /**
   * Teacher path (realtime-layer phase 2b): register message routing, the
   * connection banner, and the presence→Backbone reconcile WITHOUT
   * subscribing — channel subscriptions are owned by the realtime channel
   * manager (~shared/realtime), which reconciles them on room join/switch/
   * rename. The legacy subscribeToMessages/subscribeToPresence below remain
   * for the student app, which still owns its own subscriptions.
   * @param {object} props { channel: the room's -student channel, user }
   */
  attachRoomListeners(props) {
    this.detachRoomListeners();

    localCopyOfPubNubListeners.messageListener = {
      message: this.pubnubMessageListener,
      // No hereNow on reconnect: the channel manager re-seeds the presence
      // store, and the store subscription below follows it.
      status: this.createPubNubStatusListener(props, null, false),
    };
    window.pubnub.addListener(localCopyOfPubNubListeners.messageListener);

    // Header presence reads the room's PresenceStore, which the presence
    // bridge keeps current from PubNub presence events and the channel
    // manager seeds from hereNow, so the header makes no hereNow calls of its
    // own and agrees with the results grid.
    const { getPresenceStore } = require('~shared/realtime/presenceBridge');
    const channel = props.channel;
    const store = getPresenceStore(channel);
    let lastPresent = null;
    const onStoreChange = () => {
      const present = [];
      store.snapshot().forEach((entry, uuid) => {
        if (entry.isPresent) {
          present.push(uuid);
        }
      });
      // markAlive (every student response) also emits; only a change in who
      // is present is a presence change.
      const key = present.sort().join(',');
      if (key === lastPresent || !store.isSeeded()) {
        return;
      }
      lastPresent = key;
      presenceChanged(
        {
          occupancy: present.length,
          occupants: present.map((uuid) => ({ uuid })),
        },
        channel
      );
    };
    localCopyOfPubNubListeners.presenceStore = {
      channel,
      unsubscribe: store.subscribe(onStoreChange),
    };
    onStoreChange();
  }

  detachRoomListeners() {
    undeliveredPresence = null;
    cancelRosterRefetch();
    if (localCopyOfPubNubListeners.messageListener) {
      window.pubnub.removeListener(localCopyOfPubNubListeners.messageListener);
      delete localCopyOfPubNubListeners.messageListener;
    }
    if (localCopyOfPubNubListeners.presenceStore) {
      const { channel, unsubscribe } = localCopyOfPubNubListeners.presenceStore;
      unsubscribe();
      delete localCopyOfPubNubListeners.presenceStore;
      // On a room switch the channel manager has already unsubscribed the
      // old room, and kept its store only because this listener was on it.
      const {
        hasPresenceSubscription,
      } = require('~shared/realtime/channelManager');
      if (!hasPresenceSubscription(channel)) {
        const {
          releasePresenceStore,
        } = require('~shared/realtime/presenceBridge');
        releasePresenceStore(channel);
      }
    }
  }

  subscribeToMessages(props) {
    const subscribeOptions = {
      channels: [props.channel],
      restore: true,
    };
    localCopyOfPubNubListeners.messageListener = {
      message: this.pubnubMessageListener,
      status: this.createPubNubStatusListener(props, subscribeOptions, false),
    };

    window.pubnub.addListener(localCopyOfPubNubListeners.messageListener);
    window.pubnub.subscribe(subscribeOptions);
  }

  subscribeToPresence(props) {
    const subscribeOptions = {
      channels: [props.channel],
      restore: true,
      withPresence: true,
    };
    localCopyOfPubNubListeners.presenceListener = {
      presence: (data) => {
        presenceChanged(data, data.channel);
      },
      status: this.createPubNubStatusListener(props, subscribeOptions, true),
    };

    window.pubnub.addListener(localCopyOfPubNubListeners.presenceListener);
    window.pubnub.subscribe(subscribeOptions);
  }

  /**
   * Stop listening to a channel (both messages and presence).
   * @param {object} props An object with the following properties:
   *     {string|array} channel The channel name(s) (required)
   *     {function} callback Function to execute after unsubscribing (optional)
   */
  unsubscribe(props) {
    const { channel, callback } = props;

    window.pubnub.removeListener(localCopyOfPubNubListeners.messageListener);
    window.pubnub.removeListener(localCopyOfPubNubListeners.presenceListener);
    window.pubnub.unsubscribe({
      channels: Array.isArray(channel) ? channel : [channel],
    });

    if (callback) {
      callback();
    }
  }

  /**
   * Register a callback that will be executed each time a specific message is received from pubnub.
   * @param {object} props An object with the following properties (required):
   *     {string} message The message to listen for (use Constants.PRESENCE for presence)
   *     {function} callback A function to be executed each time the given message is received
   */
  addListener(props) {
    const { message, callback } = props;

    if (!listeners[message]) {
      listeners[message] = [callback];
    } else {
      listeners[message].push(callback);
    }

    if (message === Constants.PRESENCE && undeliveredPresence) {
      const { data, channelName } = undeliveredPresence;
      presenceChanged(data, channelName);
    }
  }

  /**
   * Remove a function from the pool of callbacks.
   * @param {object} props An object with the following properties (required):
   *     {string} message The message to stop listening for (use Constants.PRESENCE for presence)
   *     {function} callback The function to remove from the pool of callbacks
   */
  removeListener(props) {
    const { message, callback } = props;
    const callbacks = listeners[message];

    if (callbacks) {
      for (let i = 0; i < callbacks.length; i++) {
        if (callbacks[i] === callback) {
          callbacks.splice(i, 1);
          return;
        }
      }
    }
  }
}

module.exports = new Messaging();
